import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkCloseButton from '@skyscanner-internal/backpack-web/bpk-component-close-button';
import BpkImage, {
  withLazyLoading,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import BpkPrice, {
  SIZES,
  ALIGNS,
} from '@skyscanner-internal/backpack-web/bpk-component-price';
import BpkRating, {
  RATING_SIZES,
  RATING_SCALES,
} from '@skyscanner-internal/backpack-web/bpk-component-rating';
import BpkStarRating from '@skyscanner-internal/backpack-web/bpk-component-star-rating';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { redirect } from 'saddlebag-browser';

import DefaultImageSmall from '../../HotelsCommon/img/sm/default-image.svg';
import { useMBDContext } from '../../common/MBD/useMBDContext';

import type {
  HotelRatingData,
  HotelReviewsData,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/DynamicMapProps';

import STYLES from './HotelMarkerPopup.module.scss';

const documentIfExists = typeof window !== 'undefined' ? document : null;
const LazyLoadedImage = withLazyLoading(BpkImage, documentIfExists);
const IMG_WIDTH = 240;
const IMG_HEIGHT = 112;

type HotelMarkerPopupProps = {
  index: number;
  hotelName: string;
  redirectUrl: string;
  price: string;
  priceTrailingText: string;
  hotelImageUrl?: string;
  hotelImageAltText: string;
  hotelReviewsData?: HotelReviewsData;
  hotelRatingData?: HotelRatingData;
  onClose?: () => void;
};

const HotelMarkerPopup = ({
  hotelImageAltText,
  hotelImageUrl = undefined,
  hotelName,
  hotelRatingData = undefined,
  hotelReviewsData = undefined,
  index,
  onClose = undefined,
  price,
  priceTrailingText,
  redirectUrl,
}: HotelMarkerPopupProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  return (
    <BpkCard
      className={STYLES.HotelMarkerPopup}
      padded={false}
      atomic={false}
      onClick={() => {
        redirect(redirectUrl);
      }}
    >
      {onClose && (
        <BpkCloseButton
          className={STYLES.HotelMarkerPopup__closeButton}
          label="Close"
          onClick={(event: Event) => {
            onClose();
            event.stopPropagation();
          }}
          data-tracking-element-id={`${rawComponentName}_popup_close_${index}`}
          data-tracking-common-params={buildMBDParam(
            `${rawComponentName}_popup_close`,
          )}
        />
      )}
      <div className={STYLES.HotelMarkerPopup__hotelImage}>
        <LazyLoadedImage
          aspectRatio={IMG_WIDTH / IMG_HEIGHT}
          altText={hotelImageAltText}
          src={hotelImageUrl || DefaultImageSmall}
        />
      </div>
      <div className={STYLES.HotelMarkerPopup__main}>
        <div className={STYLES.HotelMarkerPopup__header}>
          <BpkText tagName="p" textStyle={TEXT_STYLES.heading5}>
            {hotelName}
          </BpkText>
        </div>
        {hotelRatingData ? (
          <div className={STYLES.HotelMarkerPopup__starRating}>
            <BpkStarRating
              rating={hotelRatingData.hotelRating}
              maxRating={5}
              ratingLabel={hotelRatingData.hotelStarsAltText}
            />
          </div>
        ) : null}
        {hotelReviewsData ? (
          <div className={STYLES.HotelMarkerPopup__reviewRating}>
            <BpkRating
              ariaLabel={hotelReviewsData.reviewRatingAriaLabel}
              value={hotelReviewsData.reviewScore}
              showScale
              title={hotelReviewsData.reviewRatingLabel}
              subtitle={hotelReviewsData.reviewNumberText}
              ratingScale={RATING_SCALES.zeroToFive}
              size={RATING_SIZES.base}
            />
          </div>
        ) : null}
        <BpkPrice
          price={price}
          size={SIZES.xsmall}
          align={ALIGNS.left}
          trailingText={priceTrailingText}
        />
      </div>
    </BpkCard>
  );
};

export default HotelMarkerPopup;
